// @flow
import * as React from 'react';
import type { CurrentUser } from 'api/currentUser';
import styled from 'styled-components';
import { isNotEmpty } from 'utils/functional';
import { trackPageView } from 'utils/analytics';
import type { PublicPage, Subsite } from 'api/website';
import type { Category, CmsTag } from 'api/cms';
import type { RouterHistory } from 'react-router';
import getContentBlock, { hasBackground } from 'components/Cms/getContentBlock';
import Box from 'components/Box';
import Tag from 'components/Tag';
import Spinner from 'components/Spinner';
import { FilestackImage } from 'components/Image';
import { SecondaryButton } from 'components/Button';
import SocialButtons from 'components/CmsSocialButtons';
import Byline from 'components/Byline';
import { AnimatedCheckmark } from 'components/Icon';
import Container from 'components/Container';
import SubscribeToSubsiteContainer from 'containers/SubscribeToSubsiteContainer';
import CmsRecommendedJobs from 'components/CmsRecommendedJobs';
import ContentLayout from 'layouts/ContentLayout';
import AuthorCard from 'components/AuthorCard';
import { H1 } from 'components/Text';
import { colors } from '../theme';

const { useEffect } = React;

const Sidebar = styled.div`
  > * {
    &:not(:first-child) {
      margin-top: 40px;
    }
  }
`;

const HeaderImage = styled.div`
  img {
    width: 100%;
  }
`;

const Categories = ({
  homePageUrl,
  categories
}: {
  homePageUrl: string,
  categories: Array<Category>
}) => (
  <>
    <Box data-qa-id="categories" display="flex" m="-6px" flexWrap="wrap">
      {categories.map(category => (
        <Tag
          key={category.slug}
          component={homePageUrl && 'a'}
          disabled={!homePageUrl && 'disabled'}
          data-qa-id="category-link"
          href={
            homePageUrl
              ? `/${homePageUrl}?categories=${
                  category.slug
                }&searchMode=true&pq=`
              : ''
          }
          m="6px"
          large
        >
          {category.name}
        </Tag>
      ))}
    </Box>
  </>
);

const Tags = ({
  homePageUrl,
  tags
}: {
  homePageUrl: string,
  tags: Array<CmsTag>
}) => (
  <>
    <Box data-qa-id="tags" display="flex" m="-6px">
      {tags.map(tag => (
        <Tag
          key={tag.slug}
          component={homePageUrl && 'a'}
          disabled={!homePageUrl && 'disabled'}
          data-qa-id="tag-link"
          href={
            homePageUrl
              ? `/${homePageUrl}?tags.slug=${tag.slug}&searchMode=true&pq=`
              : ''
          }
          m="6px"
        >
          {tag.tag}
        </Tag>
      ))}
    </Box>
  </>
);

const ContentPage = ({
  isStaff,
  content,
  subsite,
  withBanner,
  fullWidthLayout,
  loggedIn,
  currentUser,
  showModal,
  pushFlashMessage,
  updateCurrentUser,
  pageUrl,
  locale,
  homePageUrl,
  history,
  hasGradProgram,
  showListingCounts,
  gMapsErrored,
  setExposeSiteNav
}: {|
  isStaff: boolean,
  content: PublicPage,
  subsite: Subsite,
  withBanner?: boolean,
  fullWidthLayout?: boolean,
  loggedIn: boolean,
  currentUser: ?CurrentUser,
  showModal: Function,
  pushFlashMessage: Function,
  updateCurrentUser: Function,
  pageUrl: string,
  locale: string,
  homePageUrl: string,
  history: RouterHistory,
  hasGradProgram: boolean,
  showListingCounts: boolean,
  gMapsErrored: boolean,
  setExposeSiteNav: Function
|}) => {
  useEffect(
    () => {
      trackPageView('CMS Page', {
        subsite_name: subsite.name,
        page_title: content.title,
        page_type: subsite.name
      });
    },
    [content, subsite.name]
  );
  const offsetAnchors = content.contentBlocks.some(
    block =>
      block.type === 'STICKY_HEADER_LINKS_GRAD' ||
      block.type === 'STICKY_HEADER_LINKS'
  );
  const parallax = content.layout === 'FULL_WIDTH_PLUS';
  const firstBlock = content.contentBlocks[0];

  const header =
    (content.contentBlocks &&
      content.contentBlocks.length > 0 &&
      (['GRAD_CTA_HERO', 'CTA_HERO', 'DONATE'].includes(firstBlock.type) ||
        (firstBlock.type === 'SPLIT_CONTENT' &&
          firstBlock.data.appearance === 'FULL_WIDTH'))) ||
    content.heroSearch ||
    !content.showTitle ? (
      undefined
    ) : (
      <>
        {content.type === 'POST' &&
          content.categories &&
          content.categories.length > 0 && (
            <Box mb={24}>
              <Categories
                categories={content.categories}
                homePageUrl={
                  content.subsite.homePage && content.subsite.homePage.url
                }
              />
            </Box>
          )}
        <H1 m="0" textAlign={fullWidthLayout ? 'center' : 'left'}>
          {content.title}
        </H1>
        {(content.author || (content.postedDate && content.showDate)) && (
          <Box mt={4}>
            <Byline
              slug={content.author ? content.author.slug : undefined}
              name={content.author ? content.author.name : undefined}
              imageHandle={
                content.author && content.author.image
                  ? content.author.image.handle
                  : undefined
              }
              homePageUrl={
                content.subsite.homePage && content.subsite.homePage.url
              }
              postedDate={content.postedDate}
              showDate={content.showDate}
            />
          </Box>
        )}
      </>
    );

  return (
    <ContentLayout
      isStaff={isStaff}
      layout={content.layout}
      fullWidthLayout={fullWidthLayout}
      padTop={
        isNotEmpty(content.contentBlocks) &&
        !(
          [
            'STICKY_HEADER_LINKS',
            'STICKY_HEADER_LINKS_GRAD',
            'CTA_HERO',
            'GRAD_CTA_HERO',
            'IDEALIST_DAYS_MAP',
            'DONATE',
            'SIGN_UP'
          ].includes(firstBlock.type) ||
          (fullWidthLayout &&
            firstBlock.type === 'SPLIT_CONTENT' &&
            ['FULL_WIDTH', 'OVERLAPPING'].includes(
              firstBlock.data.appearance
            )) ||
          hasBackground({ block: firstBlock, fullWidthLayout })
        )
      }
      headerImage={
        content.headerImage && (
          <HeaderImage>
            <FilestackImage
              data-qa-id="cms-page-header-image"
              data={content.headerImage}
              width={fullWidthLayout ? 1152 : 952}
              alt={
                content.headerImage && content.headerImage.altText
                  ? content.headerImage.altText
                  : content.title
              }
            />
          </HeaderImage>
        )
      }
      header={header}
      socialButtons={
        content.hasSocialSharing && (
          <SocialButtons
            content={content}
            message={content.metaDescription}
            title={content.title}
            subsite={subsite.name}
          />
        )
      }
      subscribeButton={
        subsite.canSubscribe &&
        content.type === 'POST' && (
          <Box m="6px">
            <SubscribeToSubsiteContainer
              subsiteId={subsite.id}
              render={({ subscribe, isSubscribed, submitting }) => (
                <Box
                  component={SecondaryButton}
                  classes={['flex', 'align-center', 'justify-center']}
                  height={32}
                  minWidth={132}
                  size="small"
                  onClick={subscribe}
                  data-qa-id="subscribe-to-subsite"
                  disabled={isSubscribed}
                >
                  {currentUser && currentUser.isLoaded && !submitting ? (
                    <>
                      {isSubscribed && (
                        <AnimatedCheckmark
                          mr="8px"
                          stroke={colors.turtleGreen}
                        />
                      )}
                      {isSubscribed
                        ? getText('Subscribed!')
                        : getText('Subscribe')}
                    </>
                  ) : (
                    <Spinner size="small" label="Submitting" />
                  )}
                </Box>
              )}
            />
          </Box>
        )
      }
      showListingCounts={showListingCounts}
      blocks={content.contentBlocks
        .map((block, index) =>
          block.type === 'ANCHOR'
            ? {
                component: null,
                id: block.id,
                type: block.type,
                width: 'FULL',
                padding: 'DEFAULT',
                mustNotHaveDivider: true,
                hasBackground: false
              }
            : getContentBlock({
                content,
                blocks: content.contentBlocks,
                index,
                block,
                loggedIn,
                currentUser,
                showModal,
                pushFlashMessage,
                updateCurrentUser,
                pageUrl,
                locale,
                subsite,
                history,
                hasGradProgram,
                isSidebar: false,
                isPageHeader: !withBanner && index === 0,
                homePageUrl,
                pageUpdated: content.updated,
                fullWidthLayout,
                parallax,
                anchor:
                  index > 0 &&
                  content.contentBlocks[index - 1].type === 'ANCHOR'
                    ? content.contentBlocks[index - 1]
                    : undefined,
                offsetAnchors,
                gMapsErrored,
                setExposeSiteNav
              })
        )
        .filter(b => b.type !== 'ANCHOR')}
      belowBlocks={
        (content.hasRecommendedJobs ||
          (content.tags && content.tags.length > 0) ||
          content.author) && (
          <>
            {content.tags && content.tags.length > 0 && (
              <Box mb={32}>
                <Tags
                  homePageUrl={
                    content.subsite.homePage && content.subsite.homePage.url
                  }
                  tags={content.tags}
                />
              </Box>
            )}
            {content.author && (
              <AuthorCard
                author={content.author}
                homePageUrl={homePageUrl}
                postedDate={content.postedDate}
                showDate={content.showDate}
                fullWidthLayout={fullWidthLayout}
              />
            )}
            {content.hasRecommendedJobs && (
              <CmsRecommendedJobs
                headline={content.recommendedJobsHeadline}
                currentUser={currentUser}
                history={history}
              />
            )}
          </>
        )
      }
      upperSidebar={
        content.sidebar &&
        content.sidebar.contentBlocksTop.length > 0 && (
          <Sidebar>
            {content.sidebar.contentBlocksTop.map((block, index) => (
              <Container key={block.id}>
                {
                  getContentBlock({
                    content,
                    block,
                    index,
                    loggedIn,
                    currentUser,
                    showModal,
                    pushFlashMessage,
                    updateCurrentUser,
                    pageUrl,
                    locale,
                    subsite,
                    history,
                    hasGradProgram,
                    isSidebar: true,
                    homePageUrl,
                    pageUpdated: content.updated,
                    gMapsErrored,
                    setExposeSiteNav
                  }).component
                }
              </Container>
            ))}
          </Sidebar>
        )
      }
      lowerSidebar={
        content.sidebar &&
        content.sidebar.contentBlocksBottom.length > 0 && (
          <Sidebar>
            {content.sidebar.contentBlocksBottom.map((block, index) => (
              <Container key={block.id}>
                {
                  getContentBlock({
                    content,
                    block,
                    index,
                    loggedIn,
                    currentUser,
                    showModal,
                    pushFlashMessage,
                    updateCurrentUser,
                    pageUrl,
                    locale,
                    subsite,
                    history,
                    hasGradProgram,
                    isSidebar: true,
                    homePageUrl,
                    pageUpdated: content.updated,
                    gMapsErrored,
                    setExposeSiteNav
                  }).component
                }
              </Container>
            ))}
          </Sidebar>
        )
      }
    />
  );
};

export default ContentPage;
